import type { ChangeEvent } from 'react'
import type { CatalogDocumentDTO } from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { ReferenceDocumentCard } from './ReferenceDocumentCard'
import type { ReferenceListStatus } from './types'

interface Props {
  documents: CatalogDocumentDTO[]
  totalCount: number
  status: ReferenceListStatus
  error: string | null
  query: string
  onQueryChange: (value: string) => void
  onRetry: () => void
  onRequestDelete: (doc: CatalogDocumentDTO) => void
  deletingDocumentId: string | null
}

/**
 * Reference Library grid. Mirrors the documents library layout (skeleton →
 * error → empty → search-empty → grid) so the two surfaces behave identically.
 * We deliberately skip the status dropdown (reference docs are simpler) and
 * the "Use as scope" affordance (multi-context RAG comes later).
 */
export function ReferenceDocumentList({
  documents,
  totalCount,
  status,
  error,
  query,
  onQueryChange,
  onRetry,
  onRequestDelete,
  deletingDocumentId,
}: Props) {
  const isFiltering = query.trim().length > 0

  const toolbar = (
    <div className="doc-toolbar" role="search">
      <label className="doc-search">
        <Icon name="search" />
        <input
          type="search"
          value={query}
          placeholder="Buscar por título, fonte, tipo, ano…"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onQueryChange(e.target.value)
          }
          onKeyDown={(e) => {
            // Same defensive guard as DocumentList: Enter in this search must
            // never bubble out to a sibling upload form.
            if (e.key === 'Enter') e.preventDefault()
          }}
          spellCheck={false}
          aria-label="Buscar referências"
        />
      </label>
    </div>
  )

  if (status === 'loading' || status === 'idle') {
    return (
      <>
        {toolbar}
        <div className="doc-grid doc-grid-skeleton" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="doc-card doc-card-skeleton">
              <div className="skeleton-line long" />
              <div className="skeleton-line mid" />
              <div className="skeleton-line short" />
            </div>
          ))}
        </div>
      </>
    )
  }

  if (status === 'error') {
    return (
      <div className="doc-state doc-state-error" role="alert">
        <div className="doc-state-icon">
          <Icon name="close" />
        </div>
        <h3>Não foi possível carregar a biblioteca de referência</h3>
        <p>{error ?? 'Erro inesperado ao acessar o catálogo.'}</p>
        <button type="button" className="doc-state-cta" onClick={onRetry}>
          <Icon name="rotate" /> Tentar novamente
        </button>
      </div>
    )
  }

  if (totalCount === 0) {
    return (
      <div className="doc-state doc-state-empty">
        <div className="doc-state-icon">
          <Icon name="doc" />
        </div>
        <h3>Nenhuma referência cadastrada ainda</h3>
        <p>
          Use o painel ao lado para enviar protocolos, leis, guias, políticas,
          SOPs e outras bases reutilizáveis.
        </p>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <>
        {toolbar}
        <div className="doc-state doc-state-empty">
          <div className="doc-state-icon">
            <Icon name="search" />
          </div>
          <h3>Nenhuma referência corresponde à busca</h3>
          <p>Ajuste o texto para tentar novamente.</p>
          {isFiltering ? (
            <button
              type="button"
              className="doc-state-cta"
              onClick={() => onQueryChange('')}
            >
              <Icon name="close" /> Limpar filtro
            </button>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <>
      {toolbar}
      <div className="doc-grid">
        {documents.map((doc) => (
          <ReferenceDocumentCard
            key={`${doc.tenant_id}::${doc.document_id}`}
            doc={doc}
            onRequestDelete={() => onRequestDelete(doc)}
            isDeleting={deletingDocumentId === doc.document_id}
          />
        ))}
      </div>
    </>
  )
}
